<script setup lang="ts">
import { useToast } from '@solfacil/girassol';

import { useListQueryFilters } from '~/composables/useListQueryFilters';
import {
  useCreateOrderMirrorByIdMutation,
  useGetPaymentLinkLazyQuery,
  type OrderAction
} from '~/types/ecommerce/generated.codegen';

const props = withDefaults(
  defineProps<{
    clientName: string;
    orderNumber: number;
    orderActions: (OrderAction | null)[] | undefined;
    orderId: string;
    paymentMethod: string;
    innovaId: number | undefined;
    canDownloadSalesInvoice?: boolean;
    canDownloadShippingInvoices?: boolean;
    hasMultipleDeliveries?: boolean;
  }>(),
  {
    canDownloadSalesInvoice: false,
    canDownloadShippingInvoices: false,
    hasMultipleDeliveries: false,
  }
);

const emit = defineEmits<{
  'download-sales-invoice': [];
  'download-shipping-invoices': [];
}>();

const config = useRuntimeConfig();
const { getPedidosListPath } = useListQueryFilters();

const name = computed(() => props.clientName || '');
const order = computed(() => props.orderNumber || '');
const actions = computed(() => props.orderActions || []);
const enabledPaymentBtn = computed(() =>
  ['Pix', 'Boleto'].includes(props.paymentMethod)
);
const enableFinancingBtn = computed(() =>
  ['Parcelado', 'Financiamento'].includes(props.paymentMethod)
);
const { createErrorToast, createInformativeToast, createSuccessToast } =
  useToast();

const payment = computed(() => {
  return actions.value?.find(action => action?.id === 'PAY');
});

const creditCardPayment = computed(() => {
  return actions.value?.find(action => action?.id === 'OPEN_PAYMENT_LINK');
});

const mirror = computed(() => {
  return actions.value?.find(action => action?.id === 'GET_MIRROR');
});

const cancelOrder = computed(() => {
  return actions.value?.find(action => action?.id === 'CANCEL');
});

const remessaNfLabel = computed(() =>
  props.hasMultipleDeliveries
    ? 'Todas as NF-e de remessa'
    : 'NF-e de remessa',
);

const financing = computed(() => {
  return actions.value?.find(action => action?.id === 'GO_TO_FINANCING');
});

const installment = computed(() => {
  return actions.value?.find(action => action?.id === 'GO_TO_INSTALLMENT');
});

const {
  mutate: getOrderMirror,
  onDone: orderMirrorResult,
  onError: orderMirrorError,
  loading: orderMirrorLoading
} = useCreateOrderMirrorByIdMutation();
orderMirrorResult((result) => {
  const mirrorLink = result.data?.createOrderMirrorById?.url;
  if (mirrorLink) {
    navigateTo(mirrorLink, {
      external: true,
      open: { target: '_blank' }
    });
    createSuccessToast('Espelho do pedido gerado com sucesso!');
  }
});
orderMirrorError(() => {
  createErrorToast('Não foi possível gerar o espelho do pedido');
});

const loadingActions = computed(() => orderMirrorLoading.value);

const selectedOrderId = ref();
const openPaymentModal = ref(false);
const closePaymentModal = () => {
  openPaymentModal.value = false;
};
const openCancelModal = ref(false);
const closeCancelModal = () => {
  openCancelModal.value = false;
};

const creditCardPaymentLink = ref('');
const { load: getPaymentLink, loading: getPaymentLinkLoading, onResult: getPaymentLinkOnResult } =
  useGetPaymentLinkLazyQuery(
    { orderId: props.orderId },
    {
      fetchPolicy: 'network-only'
    }
  );

getPaymentLinkOnResult((result) => {
  if (result?.data) {
    const link = result?.data?.getPaymentLink?.link;
    if (link) {
      creditCardPaymentLink.value = link;
    }
  }
});

function actionDetails (item: any) {
  if (item.toLowerCase() === 'go_to_financing') {
    const financingUrl = config.public.HOST_FINANCING;
    navigateTo(`${financingUrl}/`, {
      external: true,
      open: { target: '_blank' }
    });
  }

  if (item.toLowerCase() === 'go_to_installment') {
    const financingUrl = config.public.HOST_FINANCING;
    navigateTo(`${financingUrl}/`, {
      external: true,
      open: { target: '_blank' }
    });
  }

  if (item.toLowerCase() === 'open_payment_link') {
    navigateTo(creditCardPaymentLink.value, {
      external: true,
      open: { target: '_blank' }
    });
  }

  if (item.toLowerCase() === 'pay' && payment?.value?.enabled) {
    selectedOrderId.value = props.orderId;
    openPaymentModal.value = true;
  }

  if (item.toLowerCase() === 'get_mirror' && mirror.value?.enabled) {
    createInformativeToast('Gerando espelho do pedido');
    getOrderMirror({ order_id: props.orderId });
  }

  if (item.toLowerCase() === 'billing_note' && props.canDownloadSalesInvoice) {
    emit('download-sales-invoice');
  }

  if (item.toLowerCase() === 'stock_note' && props.canDownloadShippingInvoices) {
    emit('download-shipping-invoices');
  }

  if (item.toLowerCase() === 'cancel' && cancelOrder?.value?.enabled) {
    selectedOrderId.value = props.orderId;
    openCancelModal.value = true;
  }
}

function copyPaymentLinkToClipboard () {
  const text = creditCardPaymentLink.value;
  if (text) {
    useClipboard().copy(text);
    createSuccessToast({
      type: 'success',
      description: 'Link de pagamento copiado para a área de transferência'
    });
  }
}

function goToOrdersList () {
  navigateTo(getPedidosListPath());
}

onMounted(() => {
  if (creditCardPayment.value?.enabled) {
    getPaymentLink(null, {
      orderId: props.orderId
    });
  }
});
</script>
<template>
  <div class="title-order-container">
    <div class="title-infos">
      <span class="title">Pedido: {{ name }}</span>
      <span class="subtitle">Pedido Nº {{ order }}  {{ innovaId ? `| (Nº Innova ${innovaId})` : '' }}</span>
    </div>

    <div class="actions">
      <SolButton
        v-if="enableFinancingBtn"
        id="financing"
        :variant="
          financing?.primary || installment?.primary ? 'primary' : 'secondary'
        "
        size="medium"
        class="btn-actions"
        @click="actionDetails('go_to_financing')"
      >
        <template #icon:left>
          <icon-girassol:solfacil class="icon-size-medium" />
        </template>
        {{
          paymentMethod === "Financiamento"
            ? "Ir para o Financiamento"
            : "Ir para o Parcelado"
        }}
      </SolButton>

      <SolButton
        v-if="enabledPaymentBtn"
        id="pay"
        :variant="payment?.primary ? 'primary' : 'secondary'"
        size="medium"
        class="btn-actions"
        :disabled="!payment?.enabled"
        @click="actionDetails('pay')"
      >
        <template #icon:left>
          <icon-girassol:attach-money class="icon-size-medium" />
        </template>
        Fazer Pagamento
      </SolButton>

      <SolButton
        v-if="creditCardPayment"
        id="open-payment-link"
        :variant="!payment?.primary ? 'primary' : 'secondary'"
        size="medium"
        class="btn-actions"
        :disabled="!creditCardPayment?.enabled || getPaymentLinkLoading"
        @click="actionDetails('open_payment_link')"
      >
        <template #icon:left>
          <icon-girassol:open-in-new class="icon-size-medium" />
        </template>
        Abrir link de pagamento
      </SolButton>

      <SolButton
        v-if="creditCardPayment"
        id="copy-payment-link"
        :variant="payment?.primary ? 'primary' : 'secondary'"
        size="medium"
        class="btn-actions"
        :disabled="!creditCardPayment?.enabled || getPaymentLinkLoading"
        @click="copyPaymentLinkToClipboard"
      >
        <template #icon:left>
          <icon-girassol:content-copy class="icon-size-medium" />
        </template>
        Copiar link de pagamento
      </SolButton>

      <SolMenu id="more-actions" aria-controls="menu-container-order" class="btn-menu">
        <template #label>
          <span class="label-menu sol-button-core -secondary -medium -has-icon">
            <icon-girassol:filter-list class="icon-size-medium icon-menu" />
            Mais ações
          </span>
        </template>

        <SolMenuItem
          id="billing-note"
          :class="{ '-disabled': !canDownloadSalesInvoice || loadingActions }"
          @click="actionDetails('billing_note')"
        >
          <div class="action-button">
            <icon-girassol:download class="icon" />
            <span>NF-e de venda</span>
          </div>
        </SolMenuItem>
        <SolMenuItem
          id="stock-note"
          :class="{ '-disabled': !canDownloadShippingInvoices || loadingActions }"
          @click="actionDetails('stock_note')"
        >
          <div class="action-button">
            <icon-girassol:download class="icon" />
            <span>{{ remessaNfLabel }}</span>
          </div>
        </SolMenuItem>
        <SolMenuItem
          id="get-mirror"
          :class="{ '-disabled': !mirror?.enabled || loadingActions }"
          @click="actionDetails('get_mirror')"
        >
          <div class="action-button">
            <icon-girassol:download class="icon" />
            <span>Espelho do pedido</span>
          </div>
        </SolMenuItem>
        <SolMenuItem
          id="cancel-order"
          :class="{ '-disabled': !cancelOrder?.enabled || loadingActions }"
          @click="actionDetails('cancel')"
        >
          <div class="action-button">
            <icon-girassol:cancel class="icon" />
            <span>Cancelar pedido</span>
          </div>
        </SolMenuItem>
      </SolMenu>
    </div>

    <OrdersPaymentModal
      :open="openPaymentModal"
      :order-id="selectedOrderId"
      @close="closePaymentModal"
    />
    <OrdersCancelModal
      :open="openCancelModal"
      :order-id="selectedOrderId"
      :order-number="orderNumber.toString()"
      :client-name="clientName"
      @update="goToOrdersList"
      @close="closeCancelModal"
    />
  </div>
</template>

<style lang="scss">
.title-order-container {
  @apply flex xsm:site:flex-col justify-between md:site:items-center;

  > .title-infos {
    @apply xsm:site:mb-2xs;

    .title {
      @apply fonts-heading-h2;
      @apply block;
    }

    .subtitle {
      @apply text-neutral-low-light;
    }
  }

  > .actions {
    @apply flex xsm:site:flex-col gap-micro;

    > .btn-actions {
      @apply xsm:site:w-full;
    }

    > .btn-menu {
      > button {
        @apply xsm:site:w-full;

        > .label-menu {
          @apply flex items-center;
          @apply xsm:site:w-full;

          > .icon-menu {
            @apply mr-quark;
          }
        }
      }
    }
  }
}

.action-button {
  @apply flex items-center w-max gap-nano;

  > .icon {
    @apply icon-size-small;
  }
}

.sol-menuitem-core.-disabled {
  @apply cursor-default;
  @apply text-neutral-high-dark;
  @apply hover:bg-neutral-high-pure;
}
</style>
